import * as T from 'three';
import type {PlanetHost} from '../world/planetHost';
import {cliffHit} from '@hyperlight/shared';

/** A near-miss assist, not a ladder: feet must already be within arm's reach. */
export const LEDGE = {reach:.85,minRise:.38,maxRise:1.6,minSpeed:.25,maxSpeed:15,maxFall:8,
  depth:.72,halfWidth:.27,lowDuration:.72,highDuration:1,cooldown:.4} as const;
export const ledgeDuration=(state:number):number=>state===1?LEDGE.lowDuration:LEDGE.highDuration;

/** Collision and animation share this clock. The lift precedes the crossover so
 * feet cannot be dragged through the face. Coordinates stay in the host frame. */
export class LedgeClimbMotion {
  state=0;
  age=0;
  readonly anchor=new T.Vector3();
  readonly forward=new T.Vector3();
  private cooldown=0;
  private readonly start=new T.Vector3();
  private readonly edge=new T.Vector3();
  private readonly end=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly direction=new T.Vector3();
  private readonly right=new T.Vector3();
  private readonly local=new T.Vector3();
  private readonly probe=new T.Vector3();
  private readonly next=new T.Vector3();
  private readonly previous=new T.Vector3();
  private rise=0;
  private distance=0;
  private host:PlanetHost|null=null;
  private cliffPoint=[0,0,0];
  private cliffHit=cliffHit();

  reset():void{this.state=0;this.age=0;this.cooldown=0;this.host=null;}
  cancel():void{this.state=0;this.age=0;this.cooldown=LEDGE.cooldown;this.host=null;}

  private top(host:PlanetHost,p:T.Vector3,maxR:number):number|null {
    const roof=host.settlementColliders?.standRadiusBelow(p.x,p.y,p.z,maxR)??null;
    const placed=host.homesteadStandRadius?.(p.x,p.y,p.z)??null;
    let top=placed!==null&&placed<=maxR?Math.max(roof??-Infinity,placed):roof;
    if(host.terrainSolids?.solids.length){
      const r=p.length()||1,k=(maxR+.001)/r;
      this.cliffPoint[0]=p.x*k;this.cliffPoint[1]=p.y*k;this.cliffPoint[2]=p.z*k;
      const hit=host.terrainSolids.supportBelow(this.cliffPoint,LEDGE.maxRise+.1,this.cliffHit);
      if(hit)top=Math.max(top??-Infinity,Math.hypot(...hit.point));
    }
    return top;
  }
  private solid(host:PlanetHost,p:T.Vector3,radius:number):boolean {
    if(host.settlementColliders?.occludes(p.x,p.y,p.z,radius)
      ||host.homesteadOccludes?.(p.x,p.y,p.z,radius))return true;
    if(!host.terrainSolids?.solids.length)return false;
    this.cliffPoint[0]=p.x;this.cliffPoint[1]=p.y;this.cliffPoint[2]=p.z;
    return !!host.terrainSolids.capsuleContact(this.cliffPoint,this.cliffPoint,radius,this.cliffHit);
  }
  private clear(host:PlanetHost,feet:T.Vector3):boolean {
    for(const h of [.35,.9,1.55,1.8]){
      this.probe.copy(feet).addScaledVector(this.up,h);
      if(this.solid(host,this.probe,.24))return false;
    }
    return true;
  }
  private path(t:number,out:T.Vector3):T.Vector3 {
    const low=this.rise<1.05;
    const lift=T.MathUtils.smootherstep(t,.20,.88);
    const across=T.MathUtils.smootherstep(t,low?.60:.64,1);
    // Stay outside the edge while reaching, then carry the hips over the hands.
    const catchIn=T.MathUtils.smootherstep(t,0,.22)*Math.max(0,this.distance-(low?.28:.40));
    const plant=low?.12*T.MathUtils.smoothstep(t,0,.16)*(1-T.MathUtils.smoothstep(t,.32,.5)):0;
    const settle=.08*Math.sin(Math.PI*t)*(1-T.MathUtils.smootherstep(t,.85,1));
    return out.copy(this.start).addScaledVector(this.up,this.rise*lift+settle-plant)
      .addScaledVector(this.direction,T.MathUtils.lerp(catchIn,this.distance+LEDGE.depth,across));
  }

  /** Returns 'land' only at real support. A blocked/removed ledge releases to
   * ordinary falling. Holding away/Ctrl opts out, with a re-catch cooldown. */
  step(dt:number,host:PlanetHost,pos:T.Vector3,vel:T.Vector3,worldUp:T.Vector3,wish:T.Vector3,
    enabled:boolean,grounded:boolean,drop:boolean,flying=false):'climb'|'land'|null {
    this.cooldown=Math.max(0,this.cooldown-dt);
    if(this.state&&(!enabled||drop||host!==this.host)){
      vel.addScaledVector(worldUp,-Math.max(0,vel.dot(worldUp)));this.cancel();return null;
    }
    if(!this.state){
      if(!enabled||drop||grounded||this.cooldown>0||wish.lengthSq()<.5)return null;
      const rise=vel.dot(worldUp);
      this.next.copy(vel).addScaledVector(worldUp,-rise);
      // Flight already supplies forward intent, including coasting without W.
      // A wall collision may have removed its inward velocity last tick; do
      // not require speed again once it has reached a valid, reachable lip.
      if(!flying&&(rise>4.5||rise < -LEDGE.maxFall||this.next.length()>LEDGE.maxSpeed
        ||this.next.dot(wish)<LEDGE.minSpeed))return null;
      if(flying&&this.next.dot(wish)<-.1)return null;
      this.start.copy(pos).sub(host.pose.center).applyQuaternion(host.pose.invQuat);
      this.up.copy(worldUp).applyQuaternion(host.pose.invQuat);
      this.direction.copy(wish).applyQuaternion(host.pose.invQuat).addScaledVector(this.up,-wish.dot(worldUp)).normalize();
      this.right.crossVectors(this.direction,this.up).normalize();
      const radius=this.start.length(),maxR=radius+LEDGE.maxRise;
      let distance=0,top:number|null=null;
      // Small forward steps find a lip; a binary refinement avoids a visible
      // grid snap and uses the same standable solids as normal foot collision.
      for(let d=.12;d<=LEDGE.reach+.001;d+=.09){
        this.local.copy(this.start).addScaledVector(this.direction,d);
        const r=this.top(host,this.local,maxR);
        if(r!==null&&r-radius>=LEDGE.minRise){distance=d;top=r;break;}
      }
      if(top===null)return null;
      let lo=Math.max(0,distance-.09),hi=distance;
      for(let i=0;i<7;i++){
        const mid=(lo+hi)/2;this.local.copy(this.start).addScaledVector(this.direction,mid);
        const r=this.top(host,this.local,maxR);
        if(r!==null&&Math.abs(r-top)<.12)hi=mid;else lo=mid;
      }
      this.distance=hi;this.rise=top-radius;
      this.edge.copy(this.start).addScaledVector(this.direction,hi).addScaledVector(this.up,this.rise);
      this.end.copy(this.edge).addScaledVector(this.direction,LEDGE.depth);
      // Both hands and both feet need actual support; thin fences, corners,
      // non-standable walls and low ceilings must not qualify.
      for(const forward of [.08,LEDGE.depth])for(const side of [-1,0,1]){
        this.local.copy(this.edge).addScaledVector(this.direction,forward).addScaledVector(this.right,side*LEDGE.halfWidth);
        const r=this.top(host,this.local,maxR+.1);
        if(r===null||Math.abs(r-top)>.12)return null;
        if(!this.clear(host,this.local))return null;
      }
      // Confirm a real face, not an isolated top or another floor underfoot.
      this.local.copy(this.edge).addScaledVector(this.direction,.05).addScaledVector(this.up,-.18);
      if(!this.solid(host,this.local,0))return null;
      for(let i=1;i<=32;i++)if(!this.clear(host,this.path(i/32,this.local)))return null;
      this.state=this.rise<1.05?1:2;this.age=0;this.host=host;
    }
    const support=this.top(host,this.end,this.end.length()+.2);
    if(!this.clear(host,this.end)||support===null||Math.abs(support-this.edge.length())>.15){
      vel.addScaledVector(worldUp,-Math.max(0,vel.dot(worldUp)));this.cancel();return null;
    }
    // Publish the final authored pose for one tick before releasing ownership.
    // This also makes a last-frame foot plant identical at all simulation rates.
    const finished=this.age>=ledgeDuration(this.state);
    this.age=Math.min(ledgeDuration(this.state),this.age+dt);
    const t=this.age/ledgeDuration(this.state);
    this.previous.copy(pos);
    this.path(t,this.next);
    if(!this.clear(host,this.next)){vel.addScaledVector(worldUp,-Math.max(0,vel.dot(worldUp)));this.cancel();return null;}
    pos.copy(this.next).applyQuaternion(host.pose.quat).add(host.pose.center);
    vel.copy(pos).sub(this.previous).divideScalar(Math.max(dt,1e-6));
    this.anchor.copy(this.edge).applyQuaternion(host.pose.quat).add(host.pose.center);
    this.forward.copy(this.direction).applyQuaternion(host.pose.quat);
    if(finished){vel.set(0,0,0);this.cancel();return 'land';}
    return 'climb';
  }
}
